import React, { useState, useEffect } from 'react';
import { CreateMealStyle } from '../meal.styles';
import { useDispatch, useSelector } from 'react-redux';
import { get_ingredients, get_single_meal } from '../redux/reducer';
import Details from './Details';
import Ingredients from './Ingredients';
import {
  getIngredients,
  getLoading,
  getMeal,
  getSearching
} from '../redux/selector';
import { Link } from 'react-router-dom';
import { MdChevronLeft } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import LoadingDataUi from '@app/components/loading';
import { getMealCategories } from '@app/pages/mealCategories/redux/selector';
import { get_meal_categories } from '@app/pages/mealCategories/redux/reducer';
import { get_health_conditions } from '@app/pages/health/redux/reducer';
import { getHealthConditions } from '@app/pages/health/redux/selectors';
import Nutrients from './Nutrients';
import HealthCondition from './health-condition';
import { getTags } from '@app/pages/meal-tags/redux/selectors';
import { get_tags } from '@app/pages/meal-tags/redux/reducers';
import useDebounce from '@app/Hooks/useDebounce';
import MacroNutrients from './macronutrients';
import CookingSteps from './cooking-steps';
import { ImSpinner } from 'react-icons/im';
import ProductGroupsSection from './product-groups';
import { getUnits } from '@app/pages/unit/redux/selectors';
import { get_units } from '@app/pages/unit/redux/reducers';
import useCreateMeal from '../hooks/use-create-meal';

const CreateMeal = () => {
  const dispatch = useDispatch();
  const loadMeal = useSelector(getLoading);
  const params = useParams();
  const meal = useSelector(getMeal);
  const categories = useSelector(getMealCategories);
  const healthConditions = useSelector(getHealthConditions);
  const tags = useSelector(getTags);
  const ingredients = useSelector(getIngredients);
  const [searchString, setSearchString] = useState(null);
  const debouncedTerm = useDebounce(searchString, 300);
  const searching = useSelector(getSearching);
  const [tabs, setTabs] = useState(['details', 'macronutrients']);
  const units = useSelector(getUnits);
  const {
    values,
    handleSubmit,
    setFieldValue,
    handleChange,
    handlePickImage,
    images,
    removeImage,
    mealTags,
    mealCategories,
    countries,
    health,
    isEdit,
    generatingImages,
    generateStepImage,
    loading
  } = useCreateMeal(meal, categories, tags, healthConditions, params.id);

  useEffect(() => {
    dispatch(get_units({ limit: 50 }));
  }, [dispatch]);

  useEffect(() => {
    if (debouncedTerm !== null) {
      dispatch(get_ingredients({ search: debouncedTerm }));
    }
  }, [debouncedTerm, dispatch]);

  useEffect(() => {
    dispatch(get_tags());
  }, [dispatch]);

  useEffect(() => {
    dispatch(get_meal_categories({ page: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(get_health_conditions({ page: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (params.id) {
      dispatch(get_single_meal(params.id));
    }
  }, [dispatch, params]);

  const handleIngriedentSearch = (doc) => {
    setSearchString(doc);
  };

  const options = [
    { value: 'Breakfast', label: 'Breakfast' },
    { value: 'Lunch', label: 'Lunch' },
    { value: 'Brunch', label: 'Brunch' },
    { value: 'Dinner', label: 'Dinner' }
  ];

  const resetDebouncedTerm = () => {
    setSearchString('');
  };

  const handleTabs = (doc) => {
    setTabs((prev) => {
      if (prev.includes(doc)) {
        return prev.filter((item) => item !== doc);
      } else {
        return [...prev, doc];
      }
    });
  };

  return (
    <CreateMealStyle>
      {loadMeal ? (
        <LoadingDataUi />
      ) : (
        <>
          <div className="header">
            <Link className="link" to="/meals">
              Meals
            </Link>

            <MdChevronLeft color="#605D66" />

            <p>{params.id ? 'Edit Meal' : 'Add Meal'}</p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="px-6 mt-10">
              <div className="flex flex-col gap-10 w-8/12">
                <div>
                  <div className="flex gap-2 items-center mb-4">
                    <p className="font-semibold text-[#14151A] text-[14px]">
                      Details
                    </p>
                    <div className="flex-1 border-b-[1px] border-[#DEE0E3" />
                    <div
                      className="text-[14px] font-medium cursor-pointer text-[#003333]"
                      onClick={() => handleTabs('details')}
                    >
                      Show
                    </div>
                  </div>
                  {tabs.includes('details') && (
                    <Details
                      value={values}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      images={images}
                      countries={countries}
                      handlePickImage={handlePickImage}
                      options={options}
                      params={params}
                      removeImage={removeImage}
                      categories={mealCategories}
                      health={health}
                      tags={mealTags}
                    />
                  )}
                </div>

                <div>
                  <div className="flex gap-2 items-center mb-4">
                    <p className="font-semibold text-[#14151A] text-[14px]">
                      Incompatible Medical Conditions
                    </p>
                    <div className="flex-1 border-b-[1px] border-[#DEE0E3" />
                    <div
                      className="text-[14px] font-medium cursor-pointer text-[#003333]"
                      onClick={() => handleTabs('incompatible_medical_conditions')}
                    >
                      Show
                    </div>
                  </div>
                  {tabs.includes('incompatible_medical_conditions') && (
                    <HealthCondition value={values} setFieldValue={setFieldValue} />
                  )}
                </div>

                <div>
                  <div className="flex gap-2 items-center mb-4">
                    <p className="font-semibold text-[#14151A] text-[14px]">
                      Nutrients
                    </p>
                    <div className="flex-1 border-b-[1px] border-[#DEE0E3" />
                    <div
                      className="text-[14px] font-medium cursor-pointer text-[#003333]"
                      onClick={() => handleTabs('nutrients')}
                    >
                      Show
                    </div>
                  </div>

                  {tabs.includes('nutrients') && (
                    <Nutrients value={values} setFieldValue={setFieldValue} />
                  )}
                </div>

                <div>
                  <div className="flex gap-2 items-center mb-4">
                    <p className="font-semibold text-[#14151A] text-[14px]">
                      MacroNutrients
                    </p>
                    <div className="flex-1 border-b-[1px] border-[#DEE0E3" />
                    <div
                      className="text-[14px] font-medium cursor-pointer text-[#003333]"
                      onClick={() => handleTabs('macronutrients')}
                    >
                      Show
                    </div>
                  </div>
                  {tabs.includes('macronutrients') && (
                    <MacroNutrients value={values} setFieldValue={setFieldValue} />
                  )}
                </div>

                <div>
                  <div className="flex gap-2 items-center mb-4">
                    <p className="font-semibold text-[#14151A] text-[14px]">
                      Product Group
                    </p>
                    <div className="flex-1 border-b-[1px] border-[#DEE0E3" />
                    <div
                      className="text-[14px] font-medium cursor-pointer text-[#003333]"
                      onClick={() => handleTabs('product_groups')}
                    >
                      Show
                    </div>
                  </div>
                  {tabs.includes('product_groups') && (
                    <ProductGroupsSection
                      value={values}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </div>

                <div>
                  <div className="flex gap-2 items-center mb-4">
                    <p className="font-semibold text-[#14151A] text-[14px]">
                      Ingredients List
                    </p>
                    <div className="flex-1 border-b-[1px] border-[#DEE0E3" />
                    <div
                      className="text-[14px] font-medium cursor-pointer text-[#003333]"
                      onClick={() => {
                        handleTabs('ingredients_list');
                      }}
                    >
                      Show
                    </div>
                  </div>
                  {tabs.includes('ingredients_list') && (
                    <Ingredients
                      handleIngriedentSearch={handleIngriedentSearch}
                      value={values}
                      setFieldValue={setFieldValue}
                      debouncedTerm={debouncedTerm}
                      ingredients={ingredients}
                      searching={searching}
                      resetDebouncedTerm={resetDebouncedTerm}
                      isEdit={isEdit}
                      units={units}
                    />
                  )}
                </div>

                <div>
                  <div className="flex gap-2 items-center mb-4">
                    <p className="font-semibold text-[#14151A] text-[14px]">
                      Cooking Steps
                    </p>
                    <div className="flex-1 border-b-[1px] border-[#DEE0E3" />
                    <div
                      className="text-[14px] font-medium cursor-pointer text-[#003333]"
                      onClick={() => handleTabs('cooking_steps')}
                    >
                      Show
                    </div>
                  </div>
                  {tabs.includes('cooking_steps') && (
                    <CookingSteps
                      value={values}
                      setFieldValue={setFieldValue}
                      generateStepImage={generateStepImage}
                      generatingImages={generatingImages}
                    />
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    className="grid place-items-center"
                    style={{ width: '100%' }}
                    disabled={loading}
                  >
                    {loading ? (
                      <ImSpinner className="animate-spin" size={20} />
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </>
      )}
    </CreateMealStyle>
  );
};

export default CreateMeal;
